/**
 * Agent Cube 配置向导专用 HTTP 封装。
 *
 * 与 `ModelManagementCenter/service.ts` 并存：旧模型创建流程继续走既有方法，
 * 本文件仅暴露「结构化 cube_config → YAML」链路相关接口（契约见项目方案文档 /
 * 后端 iWiki）。
 *
 * 步骤对应关系概览：
 * - Step1：getGameDatabase、getGameTableInfo
 * - Step3：checkCubeNameExists、generateYamlFromConfig
 * - Step4：testYamlConfigs → queryYamlConfigTest → publishYamlConfigs（依赖 Step3 save 返回的 uuid）
 */
import { datalabHttp } from '@dbl/services/common';
import type { AxiosRequestConfig } from 'axios';

import type {
  IGameDatabaseItem,
  IGameTableInfoApiItem,
  IGenerateYamlFromConfigRawResponse,
  IGenerateYamlFromConfigRequest,
  IGetFieldConfigRequest,
} from './types';
import type { IYamlConfigSaveParams } from '../ModelManagementCenter/types';

/**
 * 获取当前游戏下可选数据库列表（含 driver、schema 等）。
 * POST /api/v1/datalab/customSql/getGameDatabase
 */
export function getGameDatabase(params: {
  game_code: string;
}): Promise<IGameDatabaseItem[]> {
  const options: AxiosRequestConfig = {
    url: '/api/v1/datalab/customSql/getGameDatabase',
    method: 'post',
    data: params,
  };
  return datalabHttp(options) as Promise<IGameDatabaseItem[]>;
}

/**
 * 按库与 Schema 拉取数据表列表及列默认配置（维度/指标推断、默认 SQL 等）。
 * POST /api/v1/datalab/yaml-configs/getGameTableInfo
 */
export function getGameTableInfo(params: {
  game_code: string;
  database_uuid: string;
  schema: string;
}): Promise<IGameTableInfoApiItem[]> {
  const options: AxiosRequestConfig = {
    url: '/api/v1/datalab/yaml-configs/getGameTableInfo',
    method: 'post',
    data: params,
  };
  return datalabHttp(options) as Promise<IGameTableInfoApiItem[]>;
}

/**
 * 选表完成后拉取已选表字段的完整默认配置（含 sample_values 等）。
 * POST /api/v1/datalab/yaml-configs/get_field_config
 */
export function getFieldConfig(params: IGetFieldConfigRequest) {
  const options: AxiosRequestConfig = {
    url: '/api/v1/datalab/yaml-configs/get_field_config',
    method: 'post',
    data: params,
  };
  return datalabHttp(options);
}

/**
 * 根据结构化 cube_config 生成 YAML 文本（可再在编辑器中微调）。
 * POST /api/v1/datalab/yaml-configs/generate_yaml_from_config
 *
 * @param params - 通常含 game_code、schema、cube_config 等，以后端契约为准
 */
export function generateYamlFromConfig(
  params: IGenerateYamlFromConfigRequest,
): Promise<IGenerateYamlFromConfigRawResponse> {
  const options: AxiosRequestConfig = {
    url: '/api/v1/datalab/yaml-configs/generate_yaml_from_config',
    method: 'post',
    data: params,
    rawData: true,
  };
  return datalabHttp(options) as Promise<IGenerateYamlFromConfigRawResponse>;
}

/**
 * Cube / YAML 名称是否已存在（保存或发布前校验）。
 * POST /api/v1/datalab/yaml-configs/check_name_exists
 */
export function checkCubeNameExists(params: Record<string, unknown>) {
  const options: AxiosRequestConfig = {
    url: '/api/v1/datalab/yaml-configs/check_name_exists',
    method: 'post',
    data: params,
  };
  return datalabHttp(options);
}

/**
 * 保存 YAML 配置（新建 uuid 可为空；成功后从响应取 uuid 供 test/publish）。
 * POST /api/v1/datalab/yaml-configs/save
 */
export function saveYamlConfigs(params: IYamlConfigSaveParams) {
  const options: AxiosRequestConfig = {
    url: '/api/v1/datalab/yaml-configs/save',
    method: 'post',
    data: params,
  };
  return datalabHttp(options);
}

/**
 * 对已保存配置执行「查询测试」。
 * POST /api/v1/datalab/yaml-configs/query_test
 *
 * @param params.uuid - 保存草稿后返回的配置 uuid
 *
 * 使用 `rawData: true`：Promise 解析为整包 `{ code, msg, data, req }`（与 axios 的
 * `response.data` 一致）。默认 `@/utils/http` 成功时只返回内层 `data`，外层 `code`
 * 在拦截器内用于分支，不会传给调用方；见 `src/utils/http.ts` 响应拦截器。
 */
export function queryYamlConfigTest(params: { uuid: string; }) {
  const options: AxiosRequestConfig = {
    url: '/api/v1/datalab/yaml-configs/query_test',
    method: 'post',
    data: params,
    rawData: true,
  };
  return datalabHttp(options);
}

/**
 * 对已保存配置执行测试（语法 / Sample Query 等，以后端为准）。
 * POST /api/v1/datalab/yaml-configs/test
 */
export function testYamlConfigs(params: Record<string, unknown>) {
  const options: AxiosRequestConfig = {
    url: '/api/v1/datalab/yaml-configs/test',
    method: 'post',
    data: params,
  };
  return datalabHttp(options);
}

/**
 * 发布已通过测试的配置。
 * POST /api/v1/datalab/yaml-configs/publish
 */
export function publishYamlConfigs(params: Record<string, unknown>) {
  const options: AxiosRequestConfig = {
    url: '/api/v1/datalab/yaml-configs/publish',
    method: 'post',
    data: params,
  };
  return datalabHttp(options);
}
